/**
 * Read-only repository accessors exposed to UI components.
 *
 * Session 11 / CR 2026-05-27 §1.2 — `ui → persistence` is a forbidden
 * edge (UI must talk to state only). A handful of components used to
 * import `TransactionRepositoryDexie` / `VehicleRepositoryDexie` / `db`
 * directly to back live-data panels. Wrapping those in tiny
 * state-layer fetcher functions keeps the boundary clean without the
 * ceremony of a full Zustand store per panel.
 *
 * UI calls these (read-only); writes still go through the store
 * actions in `useStationStore` / `useVehicleStore`.
 */
import { db } from '@/persistence/dexie/db';
import {
  TransactionRepositoryDexie,
  VehicleRepositoryDexie,
} from '@/persistence/dexie/repositories';
import type {
  StationId,
  TransactionSnapshot,
  VehicleProfileId,
  VehicleProfileSnapshot,
} from '@/state/types';

const transactionRepo = new TransactionRepositoryDexie(db);
const vehicleRepo = new VehicleRepositoryDexie(db);

/** All transactions for a station. Returns [] on Dexie error. */
export async function listTransactionsByStation(
  stationId: StationId,
): Promise<readonly TransactionSnapshot[]> {
  try {
    return await transactionRepo.byStation(stationId);
  } catch {
    return [];
  }
}

/** Single vehicle profile by id. Returns null on miss or Dexie error. */
export async function getVehicleProfile(
  id: VehicleProfileId,
): Promise<VehicleProfileSnapshot | null> {
  try {
    return await vehicleRepo.get(id);
  } catch {
    return null;
  }
}

/**
 * Seed the vehicle library with the FEAT-015 starter presets if it's
 * empty. Returns the number of presets inserted (zero when the library
 * already has rows). The repo handle is owned by this module so the UI
 * doesn't need to know about Dexie.
 */
export async function seedVehiclesIfEmpty(): Promise<number> {
  const { seedVehiclePresetsIfEmpty } = await import('./vehicleProfilePresets');
  return seedVehiclePresetsIfEmpty(vehicleRepo);
}
